import { CommonModule } from '@angular/common';
import { Component, computed, effect, OnDestroy, OnInit, ViewChild, untracked } from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { Driver, driver } from 'driver.js';
import { AvatarModule } from 'primeng/avatar';
import { BadgeModule } from 'primeng/badge';
import { DialogModule } from 'primeng/dialog';
import { filter, Subscription } from 'rxjs';
import { environment } from '../../environments/environment.dev';
import { USER_ROLE } from '../@core/models/enums';
import { UI_FEATURE } from '../@core/models/feature-mapping';
import { IProject, IUser, SHARE_ACCESS } from '../@core/models/model';
import { AuthenticationService } from '../@core/services/authentication.service';
import { FeatureFlagService } from '../@core/services/feature-flag.service';
import { ProductWalkThroughService } from '../@core/services/product-walk-through.service';
import { SubscriptionStateService } from '../@core/services/subscription-state.service';
import { KeyboardShortcutConfig, ModifierKey } from '../@shared/models/keyboard-shortcut.models';
import { SORT_ORDER } from '../@shared/models/table.models';
import { FullNamePipe } from '../@shared/pipes/fullName.pipe';
import { SanitizeHtmlPipe } from '../@shared/pipes/sanitizeHtml.pipe';
import { TextCasePipe } from "../@shared/pipes/textCase.pipe";
import { UserAvatarPipe } from '../@shared/pipes/user-avatar-pipe';
import { KeyboardShortcutService } from '../@shared/services/keyboard-shortcut.service';
import { SVG } from '../common/svgs';
import { SIDEBAR_IDS } from '../common/utils';
import { CONTRACT_TYPE, PRODUCT_WALKTHROUGH_TYPE } from '../contract-management/models/contract-management.model';
import { ContractService, IContractResponse } from '../contract-management/services/contracts.service';
import { OrganizationSetupFormService } from '../contract-management/services/organization-setup-from.service';
import { PaymentService } from '../contract-management/services/payment.service';
import { IProjectFilter, ProjectService } from '../contract-management/services/project.service';
import { UserService } from '../contract-management/services/user.service';
import { RecentItemsService } from './services/recent-items.service';
import { WorkspaceComponent } from './components/workspace-selector/workspace.component';
import { SidebarService } from './services/sidebar.service';
import { SidebarViewService } from './services/sidebar-view.service';
import { ISideBarMenu } from './models/sidebar.model';
import { SideBarComponent } from '../@shared/components/side-bar/side-bar.component';

@Component({
  selector: 'app-app-layout',
  standalone: true,
  imports: [CommonModule, BadgeModule, SideBarComponent, WorkspaceComponent, RouterModule, SanitizeHtmlPipe, UserAvatarPipe, FullNamePipe, DialogModule, TextCasePipe, AvatarModule],
  templateUrl: './app-layout.component.html',
  styleUrl: './app-layout.component.scss'
})
export class AppLayoutComponent implements OnInit, OnDestroy {
  currentUser!: IUser;
  userRoleUpdateSubscription!: Subscription;
  walkThroughSubscription!: Subscription;
  routerSubscription!: Subscription;
  contractsUpdatedSubscription!: Subscription;
  projectsUpdatedSubscription!: Subscription;
  driver!: Driver;
  USER_ROLE = USER_ROLE;
  private sidebarToggleShortcutIds: string[] = [];

  @ViewChild(SideBarComponent) sidebarComponent?: SideBarComponent;

  // Sidebar menu items
  EXPLORE = {
    header: 'Explore',
    svg: SVG.EXPLORE,
    route: '/explore',
  }
  BOB: ISideBarMenu = {
    header: 'Ask Bob',
    svg_path: `${environment.s3Url}/inndoc-ai/assets/images/bob_agent/bob.svg`,
    route: '/bob',
    click: () => {
      this.collapseSidebar();
    }
  }
  DASHBOARD = {
    header: 'Dashboard',
    svg: SVG.DASHBOARD,
    route: '/dashboard',
  }
  PROJECTS: ISideBarMenu = {
    header: 'Projects',
    items: [],
    loading: true
  }
  DOCUMENTS: ISideBarMenu = {
    header: 'Documents',
    items: [],
    loading: true
  }
  ARCHIVED: ISideBarMenu = {
    header: 'Archived',
    items: [],
    collapsed: true,
    loading: true
  }

  sidebarMenu: ISideBarMenu[] = [];

  SVG = SVG;
  ENV = environment
  PROJECTS_LIMIT = 5;
  DOCUMENTS_LIMIT = 5;
  ARCHIVED_LIMIT = 5;
  archivedProjects: IProject[] = [];

  sideBarId = computed(() =>
    this.sidebarViewService.currentView()?.id === 'settings' ? SIDEBAR_IDS.SETTINGS : SIDEBAR_IDS.MAIN
  );

  isUserMenuOpen = false;

  isProjectsLoaded = false;
  isDocumentsLoaded = false;
  isArchivedLoaded = false;
  pendingTourInitialization = false;

  constructor(
    private organizationService: OrganizationSetupFormService,
    private paymentService: PaymentService,
    private router: Router,
    private authService: AuthenticationService,
    private projectService: ProjectService,
    private contractService: ContractService,
    private sidebarService: SidebarService,
    private sidebarViewService: SidebarViewService,
    private productWalkThroughService: ProductWalkThroughService,
    private userService: UserService,
    private keyboardShortcutService: KeyboardShortcutService,
    public subscriptionState: SubscriptionStateService,
    public features: FeatureFlagService,
    private recentItems: RecentItemsService
  ) {
    // ✅ Use Angular effect to automatically rebuild sidebar and fetch data when plan loads.
    // This ensures both the sidebar and sidebar data are always in sync with plan data,
    // and that fetchData() is never called before the plan is available from the backend.
    effect(() => {
      // Track plan changes
      const plan = this.subscriptionState.plan();
      // Only rebuild/fetch if we have subscription data.
      // untracked() prevents internal signal reads (e.g. _viewStack inside setMainView)
      // from being added as effect dependencies, which would create an infinite loop.
      if (plan) {
        untracked(() => {
          this.buildSidebarMenu();
          this.fetchData();
        });
      }
    });

    effect(() => {
      const projects = this.recentItems.projects();
      const totalRecords = this.recentItems.totalProjects();
      const loading = this.recentItems.loadingProjects();
      this.isProjectsLoaded = !loading;
      this.PROJECTS = {
        ...this.PROJECTS,
        loading,
        badge: totalRecords > 0 ? { label: `(${totalRecords})` } : undefined,
        items: projects.map(project => ({
          label: project.name,
          svg: SVG.PROJECT,
          route: '/projects/dashboard',
          queryParams: { projectId: project._id }
        }))
      };
      if (totalRecords > this.PROJECTS_LIMIT) {
        this.PROJECTS.more = { label: 'More Projects', route: '/projects', queryParams: { active: true } };
      } else if (totalRecords > 0) {
        this.PROJECTS.more = { label: 'See All', route: '/projects', queryParams: { active: true } };
      } else {
        delete this.PROJECTS.more;
      }
      this.updateSidebarItem(this.PROJECTS);
      this.checkAndInitializeTour();
    });

    effect(() => {
      const documents = this.recentItems.documents();
      const totalRecords = this.recentItems.totalDocuments();
      const loading = this.recentItems.loadingDocuments();
      this.isDocumentsLoaded = !loading;
      this.DOCUMENTS = {
        ...this.DOCUMENTS,
        loading,
        badge: totalRecords > 0 ? { label: `(${totalRecords})` } : undefined,
        items: documents.map(document => ({
          label: document.name,
          svg: SVG.DOCUMENT,
          route: '/edit-document',
          queryParams: { documentId: document._id }
        }))
      };
      if (totalRecords > this.DOCUMENTS_LIMIT) {
        this.DOCUMENTS.more = { label: 'More Documents', route: '/documents' };
      } else if (totalRecords > 0) {
        this.DOCUMENTS.more = { label: 'See All', route: '/documents' };
      } else {
        delete this.DOCUMENTS.more;
      }
      this.updateSidebarItem(this.DOCUMENTS);
      this.checkAndInitializeTour();
    });

    // Watch for subscription ended state and collapse sidebar + redirect if on blocked pages
    effect(() => {
      // ✅ Wait for subscription details to be loaded before making decisions
      // This prevents race conditions where hasSubscriptionEnded() returns true (default) because data is null
      if (!this.subscriptionState.subscriptionDetails()) {
        return;
      }

      const hasSubscriptionEnded = this.subscriptionState.hasSubscriptionEnded();
      if (hasSubscriptionEnded) {
        // Reset dialog dismissal to ensure dialog shows
        this.subscriptionState.resetDialogDismissal();

        // Collapse sidebar when subscription ends
        this.sidebarService.setSidebarVisible(this.sideBarId(), false);

        // Redirect away from any blocked page (except pricing) to explore
        // Dialog will show automatically via app.component.html
        const currentUrl = this.router.url;
        const blockedRoutes = ['/profile', '/settings', '/explore', '/bob-agent', '/projects', '/documents', '/consumption'];
        const isBlockedRoute = blockedRoutes.some(route => currentUrl.startsWith(route));

        // Only redirect if on a blocked route (not pricing or auth routes)
        if (isBlockedRoute && !currentUrl.includes('/pricing') && !currentUrl.includes('/login')) {
          // Redirect to explore - guard will ensure dialog shows
          this.router.navigate(['/explore']).catch(() => {
            // If navigation fails, try root
            this.router.navigate(['']).catch(() => { });
          });
        }
      }
    });
  }

  ngOnInit(): void {
    // Always register the main view immediately so the sidebar renders from the start
    this.sidebarViewService.setMainView({
      id: 'main',
      title: 'Main',
      menu: [],
      showFooter: true,
    });

    // Subscribe to user changes
    this.authService.currentUser.subscribe(res => {
      this.currentUser = res as IUser;

      // Build sidebar as soon as we have a user — EXPLORE is always allowed even
      // without subscription data, so the menu is never completely blank.
      // When plan() is also available the constructor effect will fire again
      // and rebuild with the full feature-gated menu.
      if (this.currentUser) {
        this.buildSidebarMenu();
      }
    });

    // Subscribe to router events to check subscription on navigation
    // Note: Uses cached subscription data - NO API CALLS
    this.routerSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        if (environment.enableSubscription) {
          this.checkSubscriptionAccess();
        }
      });

    // Initialize walkthrough
    const tourType = this.currentUser?.productWalkThrough?.find(
      walkthrough => walkthrough.type === PRODUCT_WALKTHROUGH_TYPE.HOME
    );
    if (!tourType?.status && this.router.url.startsWith('/')) {
      this.pendingTourInitialization = true;
      this.checkAndInitializeTour();
    }

    // Subscribe to walkthrough reset events
    this.walkThroughSubscription = this.productWalkThroughService.walkThroughReset$.subscribe(type => {
      if (type === PRODUCT_WALKTHROUGH_TYPE.HOME) {
        this.initializeTour();
      }
    });

    // Register keyboard shortcut to toggle sidebar (Meta/Cmd + S)
    this.registerSidebarToggleShortcut();
  }

  /**
   * Register keyboard shortcut to toggle sidebar
   * Uses Meta/Cmd + S (Mac) or Ctrl + S (Windows/Linux) key combination
   */
  private registerSidebarToggleShortcut(): void {
    const toggleHandler = (event: KeyboardEvent) => {
      this.sidebarService.toggleSidebar(this.sideBarId());
    };

    // Register for Mac (Meta/Cmd + S)
    const macShortcut: KeyboardShortcutConfig = {
      id: 'toggle-sidebar-mac',
      keys: 's',
      modifiers: [ModifierKey.CMD],
      handler: toggleHandler,
      description: 'Toggle sidebar (Mac)',
      context: 'app-layout',
      preventDefault: true
    };

    // Register for Windows/Linux (Ctrl + S)
    const windowsShortcut: KeyboardShortcutConfig = {
      id: 'toggle-sidebar-windows',
      keys: 's',
      modifiers: [ModifierKey.CTRL],
      handler: toggleHandler,
      description: 'Toggle sidebar (Windows/Linux)',
      context: 'app-layout',
      preventDefault: true
    };

    // Store both registration IDs for cleanup
    const macId = this.keyboardShortcutService.register(macShortcut);
    const windowsId = this.keyboardShortcutService.register(windowsShortcut);

    // Store both IDs for proper cleanup
    this.sidebarToggleShortcutIds = [macId, windowsId];

    // Subscribe to contract updates to update the documents list
    this.contractsUpdatedSubscription = this.contractService.contractsUpdated.subscribe(() => {
      if (this.features.checkFeature(UI_FEATURE.DOCUMENTS).allowed) {
        this.getDocuments();
      }
    });

    // Subscribe to project updates to update the projects list and archived projects list
    this.projectsUpdatedSubscription = this.projectService.projectsUpdated.subscribe(() => {
      if (this.features.checkFeature(UI_FEATURE.PROJECTS).allowed) {
        this.getProjects();
      }
      if (this.features.checkFeature(UI_FEATURE.ARCHIVED).allowed) {
        this.getArchivedProjects();
      }
    });
  }

  isAskBobVisible(): boolean {
    const settings = this.currentUser?.agentModeSettings;
    if (!settings) return true;
    return settings.askMode || settings.agentMode;
  }

  get canSeeSettings() {
    return [SHARE_ACCESS.OWNER, SHARE_ACCESS.ADMIN, SHARE_ACCESS.VIEW, SHARE_ACCESS.COMMENT].includes(this.currentUser.workspaceRole) || [USER_ROLE.ADMIN, USER_ROLE.SUPER_ADMIN].includes(this.currentUser.role)
  }

  get canCreateProjects() {
    if ([SHARE_ACCESS.OWNER, SHARE_ACCESS.ADMIN].includes(this.currentUser.workspaceRole)) {
      return true;
    }
    if (this.currentUser.currentWorkspace && this.currentUser.workspaceRole &&
      ![SHARE_ACCESS.OWNER, SHARE_ACCESS.ADMIN].includes(this.currentUser.workspaceRole)) {
      return false;
    }
    if (!this.currentUser.currentWorkspace && [USER_ROLE.ADMIN, USER_ROLE.SUPER_ADMIN].includes(this.currentUser.role)) {
      return true;
    }
    return false;
  }

  /**
   * 🎯 Build sidebar menu using new feature mapping system
   * 
   * This method checks each UI feature against the plan and builds the menu accordingly.
   * It uses the centralized feature mapping configuration for consistency.
   */
  private buildSidebarMenu() {
    // ✅ Check feature availability using the new mapping system
    const askBobAccess = this.features.checkFeature(UI_FEATURE.ASK_BOB); // Bob v1 - checks BOB_V1 features

    const generalAgentAccess = this.features.checkFeature(UI_FEATURE.GENERAL_AGENT); // Bob v2.0 - checks AGENT features
    const projectsAccess = this.features.checkFeature(UI_FEATURE.PROJECTS);
    const documentsAccess = this.features.checkFeature(UI_FEATURE.DOCUMENTS);
    const archivedAccess = this.features.checkFeature(UI_FEATURE.ARCHIVED);
    const exploreAccess = this.features.checkFeature(UI_FEATURE.EXPLORE);

    // ✅ Clear any existing create actions first
    delete this.PROJECTS.create;
    delete this.DOCUMENTS.create;

    // ✅ Add create actions only if:
    // 1. User has permissions (canCreateProjects)
    // 2. Feature is in plan (projectsAccess.allowed)
    // 3. Payment is active or trial is active (when payment checks are re-enabled)
    if (this.currentUser && this.canCreateProjects && projectsAccess.allowed) {
      this.PROJECTS.create = {
        label: 'Create Project',
        svg: SVG.CREATE_PROJECT,
        route: '/projects',
        queryParams: {
          create: 'true',
          active: 'true'
        }
      };
    }
    this.DOCUMENTS.create = {
      label: 'Create Document',
      svg: SVG.CREATE_DOCUMENT,
      route: '/documents',
      queryParams: {
        create: 'true'
      }
    }

    // ✅ Build sidebar menu based on feature availability
    const menuItems: ISideBarMenu[] = [
      // Explore - Always show (content inside will be feature-gated)
      ...(exploreAccess.allowed ? [this.EXPLORE] : []),

      // Ask Bob v2.0 (General Agent) - Show if AGENT features exist in plan AND at least one mode is enabled
      ...(generalAgentAccess.allowed && this.isAskBobVisible() ? [this.BOB] : []),

      // Projects - Show if any INN_DOC features exist in plan
      ...(projectsAccess.allowed ? [this.PROJECTS] : []),

      // Archived - Show if any INN_DOC features exist in plan
      ...(archivedAccess.allowed ? [this.ARCHIVED] : []),

      // Documents - Show if any INN_DOC features exist in plan
      ...(documentsAccess.allowed ? [this.DOCUMENTS] : [])
    ];

    // ✅ Apply locked state to all menu items if sidebar is locked
    // Note: Visual locking is handled by CSS (.sidebar-locked class)
    // Route prevention is handled by route guard
    // We just mark items as locked for reference
    const isLocked = this.subscriptionState.isSidebarLocked();
    if (isLocked) {
      this.applyLockedState(menuItems);
    }

    this.sidebarMenu = menuItems;

    // Push the main view into the shared sidebar view service
    this.sidebarViewService.setMainView({
      id: 'main',
      title: 'Main',
      menu: menuItems,
      showFooter: true,
    });
  }

  /**
   * 🔒 Apply locked state to menu items (prevents navigation at route level)
   * Visual locking is handled by CSS (.sidebar-locked class on container)
   */
  private applyLockedState(menuItems: ISideBarMenu[]): void {
    menuItems.forEach(menu => {
      menu.locked = true;
      // Remove routes to prevent navigation (route guard will also catch this)
      if (menu.items) {
        menu.items.forEach(item => {
          if (item.route) {
            item.route = '';
          }
        });
      }
      if (menu.create?.route) {
        menu.create.route = '';
      }
      if (menu.more?.route) {
        menu.more.route = '';
      }
    });
  }

  /**
   * 🚀 Update specific sidebar item without rebuilding entire menu
   * More efficient for data updates that don't affect feature availability
   */
  private updateSidebarItem(itemToUpdate: ISideBarMenu) {
    const index = this.sidebarMenu.findIndex(item => item.header === itemToUpdate.header);
    if (index !== -1) {
      this.sidebarMenu[index] = itemToUpdate;
      // Keep the sidebar view service in sync
      this.sidebarViewService.updateViewMenu('main', [...this.sidebarMenu]);
    }
  }

  fetchData() {
    // Only fetch data for features that are in the user's plan
    if (this.features.checkFeature(UI_FEATURE.PROJECTS).allowed) {
      this.getProjects();
    }
    if (this.features.checkFeature(UI_FEATURE.DOCUMENTS).allowed) {
      this.getDocuments();
    }
    if (this.features.checkFeature(UI_FEATURE.ARCHIVED).allowed) {
      this.getArchivedProjects();
    }
  }

  getDocuments(skip = 0, limit = this.DOCUMENTS_LIMIT) {
    this.recentItems.loadDocuments(skip, limit);
  }

  getProjects(skip = 0, limit = this.PROJECTS_LIMIT) {
    this.recentItems.loadProjects(skip, limit);
  }

  getArchivedProjects(skip = 0, limit = this.ARCHIVED_LIMIT) {
    this.ARCHIVED.loading = true;
    this.isArchivedLoaded = false;

    const filters: IProjectFilter = {
      active: false,
      sortField: 'updatedAt',
      sortOrder: SORT_ORDER.DESC
    };
    this.projectService.getProjects(skip, limit, filters).subscribe(res => {
      this.ARCHIVED.loading = false;
      this.isArchivedLoaded = true;

      if (res.success) {
        this.archivedProjects = res.data.projects;
        const totalRecords = res.data.totalRecords || 0;
        this.ARCHIVED = {
          ...this.ARCHIVED,
          badge: totalRecords > 0 ? { label: `(${totalRecords})` } : undefined,
          collapsed: true,
          items: this.archivedProjects.map(project => ({
            label: project.name,
            svg: SVG.PROJECT,
            route: '/projects/dashboard',
            queryParams: {
              projectId: project._id,
              archived: true
            }
          }))
        }
        if (totalRecords > this.ARCHIVED_LIMIT) {
          this.ARCHIVED.more = {
            label: 'More Archived',
            route: '/projects',
            queryParams: {
              active: false
            }
          }
        } else if (totalRecords > 0) {
          this.ARCHIVED.more = {
            label: 'See All',
            route: '/projects',
            queryParams: {
              active: false
            }
          }
        } else {
          delete this.ARCHIVED.more;
        }
        // ✅ Update just the Archived item in sidebar (more efficient)
        this.updateSidebarItem(this.ARCHIVED);
      } else {
        this.archivedProjects = [];
        this.ARCHIVED = {
          ...this.ARCHIVED,
          badge: undefined,
          items: []
        }
        delete this.ARCHIVED.more;
        // ✅ Update just the Archived item in sidebar
        this.updateSidebarItem(this.ARCHIVED);
      }
      this.checkAndInitializeTour();
    });
  }

  signOut() {
    this.authService.logout();
  }

  openSettings() {
    //settings will be opened from router
    //just collapsing the side bar
    this.collapseSidebar();
  }

  collapseSidebar() {
    // No longer collapses sidebar when navigating to sub-views (bob/settings).
    // The sidebar now morphs its content instead. Kept for backwards compatibility.
  }


  checkAndInitializeTour() {
    if (!this.pendingTourInitialization) {
      return;
    }

    const allDataLoaded = this.isProjectsLoaded &&
      this.isDocumentsLoaded &&
      this.isArchivedLoaded;

    if (!allDataLoaded) {
      return;
    }

    const elementsToWait = this.getRequiredTourElements();

    Promise.all(elementsToWait.map(selector => this.waitForElement(selector)))
      .then(() => {
        setTimeout(() => {
          this.pendingTourInitialization = false;
          this.initializeTour();
        }, 200);
      })
      .catch(() => {
        this.pendingTourInitialization = false;
        this.initializeTour();
      });
  }


  waitForElement(selector: string, timeout = 5000): Promise<void> {
    return new Promise((resolve, reject) => {
      if (document.querySelector(selector)) {
        resolve();
        return;
      }
      const interval = setInterval(() => {
        if (document.querySelector(selector)) {
          clearInterval(interval);
          resolve();
        }
      }, 100);
      setTimeout(() => {
        clearInterval(interval);
        reject(new Error(`Element not found: ${selector}`));
      }, timeout);
    });
  }

  getRequiredTourElements(): string[] {
    const elements: string[] = [
      '.workspace-selector',
      '[href="/documents?create=true"]',
      '[step-id="support-menu"]',
      '[href="/profile"]'
    ];

    if (this.PROJECTS.items?.length && this.PROJECTS.more) {
      elements.push('[href="/projects?active=true"]');
    }

    if (this.ARCHIVED.items?.length && this.ARCHIVED.more) {
      elements.push('[href="/projects?active=false"]');
    }

    if (this.DOCUMENTS.items?.length && this.DOCUMENTS.more) {
      elements.push('[href="/documents"]');
    }

    if (this.canSeeSettings) {
      elements.push('[href="/settings"]');
    }

    return elements;
  }

  /**
   * Check subscription access on route changes
   * Uses cached subscription data - NO API CALLS
   * Note: Route protection is now handled by SubscriptionGuard
   * This method is kept for any additional subscription-related checks if needed
   */
  private checkSubscriptionAccess() {
    // Skip subscription check if on pricing page
    if (this.router.url.includes('/pricing')) {
      return;
    }

    // Check if subscription is loaded
    if (!this.subscriptionState.subscriptionDetails()) {
      return;
    }

    // Route protection is handled by SubscriptionGuard
    // Feature-based access is handled by sidebar menu (only shows available features)
  }

  /**
   * Refresh subscription details after payment or subscription changes
   * Call this method when you know subscription has been updated
   */
  /**
   * Refresh subscription details after payment or plan changes.
   * ⚠️ Only call this when subscription has actually changed (after payment, upgrade, etc.)
   * 
   * @example
   * // After successful payment
   * onPaymentSuccess() {
   *   this.refreshSubscription();
   * }
   */
  public refreshSubscription() {
    this.subscriptionState.refreshSubscriptionDetails().subscribe(() => {
      if (this.subscriptionState.plan()) {
        // ✅ Rebuild sidebar with new subscription data
        this.buildSidebarMenu();
        this.checkSubscriptionAccess();
      }
    });
  }




  initializeTour() {
    const steps: any[] = [];

    this.sidebarService.setSidebarVisible(this.sideBarId(), true);

    const openMenuSection = (label: string) => {
      if (this.sidebarComponent?.isMenuCollapsed(label)) {
        const el = Array.from(document.querySelectorAll('.list-none li .flex.items-center.cursor-pointer')).find(el => el.textContent?.includes(label)) as HTMLElement; el?.click();
      }
    };

    openMenuSection('Projects');
    openMenuSection('Archived');
    openMenuSection('Documents');

    steps.push({
      element: '.workspace-selector',
      popover: {
        title: 'Workspace',
        description: 'View all available workspaces, switch between them, and manage workspace-specific projects and documents from one place.',
        side: 'right',
        align: 'start',
      }
    });

    if (this.PROJECTS.items?.length && this.PROJECTS.more) {
      steps.push({
        element: `[href="/projects?active=true"]`,
        popover: {
          title: 'Projects',
          description: 'Browse all your active projects in one view for quick access and management.',
          side: 'right',
          align: 'start',
        }
      });
    }

    if (this.ARCHIVED.items?.length && this.ARCHIVED.more) {
      steps.push({
        element: `[href="/projects?active=false"]`,
        popover: {
          title: 'Archived Projects',
          description: 'Access and restore deleted projects whenever needed.',
          side: 'right',
          align: 'start'
        }
      });
    }

    steps.push({
      element: `[href="/documents?create=true"]`,
      popover: {
        title: 'Create Document',
        description: 'Add or create documents and instantly unlock workflows such as generation, comparison, AI-assisted answers, and content enhancement.',
        side: 'right',
        align: 'start'
      }
    });

    if (this.DOCUMENTS.items?.length && this.DOCUMENTS.more) {
      steps.push({
        element: `[href="/documents"]`,
        popover: {
          title: 'Documents',
          description: 'View, sort, and manage all documents created or uploaded in your workspace.',
          side: 'right',
          align: 'start'
        }
      });
    }

    steps.push({
      element: `[step-id="support-menu"]`,
      popover: {
        title: 'Raise a Request',
        description: 'Discover and explore various features and capabilities of the platform.',
        side: 'right',
        align: 'start',
        onNextClick: () => {
          // ready up the sidebar footer for the next step
          const footerToggler = document.querySelector('a[pstyleclass="@prev"]') as HTMLElement | null;
          if (footerToggler) {
            footerToggler.click();
          }
          this.driver.moveNext();
        }
      }
    });

    if (this.canSeeSettings) {
      steps.push({
        element: `[href="/settings"]`,
        popover: {
          title: 'Settings',
          description: 'Access and manage your organization\'s global settings.',
          side: 'right',
          align: 'start',
          onPrevClick: () => {
            this.isUserMenuOpen = false;
            this.driver.movePrevious();
          }
        },
      });
    }

    steps.push({
      element: `[href="/profile"]`,
      popover: {
        title: 'Profile Settings',
        description: 'Manage your profile information and preferences.',
        side: 'right',
        align: 'start',
        onPrevClick: () => {
          this.isUserMenuOpen = false;
          this.driver.movePrevious();
        }
      }
    });

    this.driver = driver({
      stageRadius: 14,
      showProgress: true,
      nextBtnText: 'Next',
      prevBtnText: 'Previous',
      allowClose: true,
      onCloseClick: () => {
        this.cleanupTourUI();
        this.completeTour();
      },
      onDestroyed: () => {
        this.cleanupTourUI();
        this.completeTour();
      },
      popoverClass: 'custom-popover',
      showButtons: ['next', 'previous'],
      onHighlightStarted: (element) => {
        if (element && element.getAttribute('href') === '/documents') {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else if (element && (element.getAttribute('href') === '/settings' || element.getAttribute('href') === '/profile')) {
          this.isUserMenuOpen = true;
        } else {
          this.isUserMenuOpen = false;
        }
      },
      steps: steps.filter(step => {
        if (!step.element) return false;
        return document.querySelector(step.element) !== null;
      })
    });
    setTimeout(() => this.driver.drive(), 300);
  }


  toggleUserMenu() {
    this.isUserMenuOpen = !this.isUserMenuOpen;
  }

  completeTour() {
    // Update tour completion in backend
    const data = { type: PRODUCT_WALKTHROUGH_TYPE.HOME, status: true };
    this.userService.updateWalkThrough(data).subscribe(() => {
      // Tour completed successfully
    });
  }

  ngOnDestroy(): void {
    // Unregister keyboard shortcuts individually
    this.sidebarToggleShortcutIds.forEach(id => {
      this.keyboardShortcutService.unregister(id);
    });
    this.sidebarToggleShortcutIds = [];

    if (this.walkThroughSubscription) {
      this.walkThroughSubscription.unsubscribe();
    }
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
    if (this.contractsUpdatedSubscription) {
      this.contractsUpdatedSubscription.unsubscribe();
    }
    if (this.projectsUpdatedSubscription) {
      this.projectsUpdatedSubscription.unsubscribe();
    }
    if (this.driver) {
      this.driver.destroy();
    }
  }

  /**
   * Cleanup tour UI by closing workspace and sidebar footer
   */
  cleanupTourUI() {
    // CLOSE SIDEBAR FOOTER
    const footerToggler = document.querySelector('a[pstyleclass="@prev"]') as HTMLElement | null;
    if (footerToggler) {
      const footerList = footerToggler.previousElementSibling as HTMLElement | null;
      if (footerList && !footerList.classList.contains('hidden')) {
        footerToggler.click();
      }
    }
  }


}

